import React, { useEffect, useRef, useCallback, useState } from "react";
import { Box } from "@mui/material";
import { useTypedSelector } from "../../hooks";
import { getIsConversationWorking } from "../../store/conversation.tsx";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";

interface ConversationProps {
  children?: React.ReactNode;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

const Conversation: React.FC<ConversationProps> = ({
  children,
  scrollContainerRef,
}) => {
  // Follow-scroll only cares about the conversation currently being viewed.
  const isStreaming = useTypedSelector((state) =>
    getIsConversationWorking(
      state,
      state.conversationState.selectedConversation,
    ),
  );

  // Track if user has manually scrolled during streaming
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const lastScrollTop = useRef<number>(0);
  const scrollTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  // refs used for height measuring
  const lastChildRef = useRef<HTMLElement>(null);
  const [paddingBottom, setPaddingBottom] = useState(0);

  // measure container vs last child
  useEffect(() => {
    const updatePadding = () => {
      if (!scrollContainerRef?.current || !lastChildRef.current) return;
      const boxHeight = scrollContainerRef.current.clientHeight;
      const lastHeight = lastChildRef.current.clientHeight;
      const pb = Math.max(boxHeight - lastHeight, 20);
      setPaddingBottom(pb);
    };

    updatePadding();

    // watch window resize
    window.addEventListener("resize", updatePadding);
    return () => window.removeEventListener("resize", updatePadding);
  }, [children, isStreaming]);

  // Calculate the marginTop offset using consistent top margin
  const getMarginTopOffset = () => {
    return SETTINGS.topMargin; // Consistent 40px top margin across all screens
  };

  // Handle user scroll events
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef?.current) return;

    const container = scrollContainerRef.current;
    const currentScrollTop = container.scrollTop;
    const maxScrollTop = container.scrollHeight - container.clientHeight;

    // Detect if user is at bottom (with small tolerance for rounding)
    const isAtBottom = Math.abs(currentScrollTop - maxScrollTop) < 10;

    // During streaming: detect user-initiated scroll away from bottom
    if (
      isStreaming &&
      currentScrollTop < lastScrollTop.current &&
      !isAtBottom
    ) {
      setUserHasScrolled(true);
    }

    // Reset user scroll flag only when they manually scroll back to bottom
    // This allows auto-scroll to resume when user explicitly goes to bottom
    if (isAtBottom && userHasScrolled) {
      // Add a small delay to avoid immediate reset during rapid scroll events
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      scrollTimeout.current = setTimeout(() => {
        setUserHasScrolled(false);
      }, 100);
    }

    lastScrollTop.current = currentScrollTop;
  }, [scrollContainerRef, isStreaming, userHasScrolled]);

  // Create the scroll callback (only auto-scroll if user hasn't manually scrolled)
  const scrollToExchange = useCallback(
    (element: HTMLElement | null) => {
      if (element && scrollContainerRef?.current && !userHasScrolled) {
        const containerMarginTop = getMarginTopOffset();
        const targetScrollTop = element.offsetTop - containerMarginTop;
        // Use smooth scrolling so the user can see the movement instead of a jump
        scrollContainerRef.current.scrollTo({
          top: Math.max(0, targetScrollTop - 10),
          behavior: "smooth",
        });
      }
    },
    [scrollContainerRef, getMarginTopOffset, userHasScrolled],
  );

  // Only reset user scroll flag when streaming starts (not when it ends)
  const prevIsStreaming = useRef(isStreaming);
  useEffect(() => {
    // If streaming just started, reset the flag
    if (isStreaming && !prevIsStreaming.current) {
      setUserHasScrolled(false);
    }
    // If streaming just ended and user had scrolled, keep their position
    // (don't reset userHasScrolled to false)

    prevIsStreaming.current = isStreaming;
  }, [isStreaming]);

  // Add scroll event listener
  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (container) {
      container.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        container.removeEventListener("scroll", handleScroll);
        if (scrollTimeout.current) {
          clearTimeout(scrollTimeout.current);
        }
      };
    }
  }, [handleScroll, scrollContainerRef]);

  // Ref callback for the last exchange
  const lastExchangeRef = useCallback(
    (node: HTMLElement | null) => {
      if (node) {
        scrollToExchange(node);
        lastChildRef.current = node;
      }
    },
    [scrollToExchange],
  );

  // Clone children and add ref to the last exchange
  const childrenArray = React.Children.toArray(children);
  const modifiedChildren = childrenArray.map((child, index) => {
    if (React.isValidElement(child) && index === childrenArray.length - 1) {
      // Pass ref to the last child if it's the last exchange
      return React.cloneElement(child, {
        ...(child.props as any),
        lastExchangeRef,
      } as any);
    }
    return child;
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        paddingTop: "0px", // Remove top padding to align with consistent top margin
        paddingBottom: `${paddingBottom}px`,
        width: "100%",
        textAlign: "left",
      }}
    >
      {modifiedChildren}
    </Box>
  );
};

export default Conversation;
